import { GrpcCurrency } from '@m/types/grpc/GrpcTypes';
import { google } from '@metropolis-io/atg-edge-api';

type Money = google.type.Money;

export function formatGrpcToCurrency(currency?: GrpcCurrency): number {
  const { units = '0', nanos = 0 } = currency || {};
  const unitsAsNumber = parseFloat(units) || 0;
  const cents = Math.abs(nanos) / 1e9;

  return unitsAsNumber >= 0 ? unitsAsNumber + cents : unitsAsNumber - cents;
}

/** Resolve the currency symbol for a given ISO 4217 currency code (e.g. USD → "$", EUR → "€"). */
export function getCurrencySymbol(currencyCode: string): string {
  const code = currencyCode?.trim() || 'USD';
  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    });
    const parts = formatter.formatToParts(0);
    const currencyPart = parts.find((p) => p.type === 'currency');
    return currencyPart?.value ?? '$';
  } catch {
    return '$';
  }
}

/** Format GrpcCurrency into display parts: dollars (e.g. "$12" or "€12") and cents (e.g. ".36"). */
export function formatGrpcCurrencyToPriceParts(currency?: GrpcCurrency): {
  cents: string;
  dollars: string;
} {
  const amount = formatGrpcToCurrency(currency);
  const code = currency?.currencyCode ?? 'USD';
  const symbol = getCurrencySymbol(code);
  const clamped = Math.max(0, amount);
  const whole = Math.floor(clamped);
  const centsVal = Math.round((clamped - whole) * 100);
  const centsPadded = centsVal.toString().padStart(2, '0');
  return {
    cents: `.${centsPadded}`,
    dollars: `${symbol}${whole}`,
  };
}

export function formatCurrencyToGrpc(amount: number, currencyCode?: string): GrpcCurrency {
  const units = Math.floor(amount);
  const nanos = Math.round((amount - units) * 1e9);
  return {
    units: units.toString(),
    nanos,
    currencyCode: currencyCode || 'USD',
  };
}

export function convertMoneyToGrpcCurrency(
  money: Money | null | undefined,
): GrpcCurrency | undefined {
  if (!money) {
    return undefined;
  }

  return {
    currencyCode: money.currencyCode || 'USD',
    units: typeof money.units === 'string' ? money.units : String(money.units ?? 0),
    nanos: money.nanos ?? 0,
  };
}

export function sumGrpcCurrencies(currencies: GrpcCurrency[]): GrpcCurrency {
  if (currencies.length === 0) {
    return { currencyCode: 'USD', units: '0', nanos: 0 };
  }

  const { totalUnits, totalNanos, currencyCode } = currencies.reduce(
    (acc, curr) => {
      acc.totalUnits += parseInt(curr.units, 10);
      acc.totalNanos += curr.nanos;
      return acc;
    },
    {
      totalUnits: 0,
      totalNanos: 0,
      currencyCode: currencies[0].currencyCode,
    },
  );

  const extraUnits = Math.floor(totalNanos / 1000000000);
  const finalUnits = totalUnits + extraUnits;
  const finalNanos = totalNanos % 1000000000;

  return {
    currencyCode,
    units: finalUnits.toString(),
    nanos: finalNanos,
  };
}

export function subtractGrpcCurrencies(
  minuend: GrpcCurrency,
  subtrahend: GrpcCurrency,
): GrpcCurrency {
  const minuendUnits = parseInt(minuend.units, 10);
  const subtrahendUnits = parseInt(subtrahend.units, 10);

  const minuendNanos = minuend.nanos;
  const subtrahendNanos = subtrahend.nanos;

  let resultUnits = minuendUnits - subtrahendUnits;
  let resultNanos = minuendNanos - subtrahendNanos;

  if (resultNanos < 0) {
    resultUnits -= 1;
    resultNanos += 1000000000;
  }

  return {
    currencyCode: minuend.currencyCode,
    units: resultUnits.toString(),
    nanos: resultNanos,
  };
}
